/*
 * 跨 feature 的展示原语。
 *
 * 收录标准（components/README.md）：至少被两个 feature 用到、不含业务规则、
 * 不发请求、不读 Store。这里的东西只知道「怎么画」。
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import { IconClose } from "./Icons";

/* -------------------------------------------------------------------------- */
/* 按钮                                                                        */
/* -------------------------------------------------------------------------- */

type ButtonTone = "primary" | "ghost" | "quiet" | "danger";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: ButtonTone;
  block?: boolean;
  icon?: ReactNode;
}

export function Button({
  tone = "ghost",
  block = false,
  icon,
  children,
  className = "",
  type = "button",
  ...rest
}: ButtonProps) {
  const classes = [
    "btn",
    `btn--${tone}`,
    block ? "btn--block" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button type={type} className={classes} {...rest}>
      {icon}
      {children}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* 二级页面外壳                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * 二级页面容器。
 *
 * 版式照旧版的弹层来：**铺满整个屏幕**，顶上一行「图标 + 标题 + 问号」，
 * 右上角一个关闭叉。没有英文小标题，没有常驻的一段说明文字。
 *
 * ⚠ 三处都踩过，别改回去：
 *
 *   ① **不要留顶部空隙。** 这里曾经写 `margin-top: 64px` 想让立绘露一截，
 *      结果是页面顶上一条空白，下面一块面板，看着像没加载完。
 *   ② **不要 eyebrow。** MEMORY / FAVORITES / Profile 那种英文小标题对用户
 *      没有任何信息量，中文标题已经说清楚了。
 *   ③ **说明文字收进问号里。** 常驻一段解释会把列表挤下去半屏，而说明只在
 *      第一次看时有用。旧版就是标题旁边一个 `HelpModal` 问号。
 *
 * `actions` 只应在**读不到数据**时传「重试」。正常有数据时摆一个重试按钮，
 * 等于暗示这页可能是坏的。
 *
 * `closeTo` 是那颗叉去哪。默认回对话（一级页只有一层，叉就是「关掉」）。
 * 回忆点滴那三层要逐层往回：日记列表 → 某一天 → 某段对话，每层传上一层的
 * 路径。**不要用 `navigate(-1)`**——从分享链接直接进第三层时，历史里
 * 没有上一层，退回去会离开这个站点。
 */
export function PageShell({
  icon,
  title,
  help,
  actions,
  closeTo = "/",
  children,
}: {
  /** 标题左边那个图标，与右上角图标栏里的那个保持一致。 */
  icon?: ReactNode;
  title: string;
  /** 问号里的说明。缈缈口吻，不要写成产品文档。 */
  help?: ReactNode;
  actions?: ReactNode;
  /** 右上角那颗叉去哪。默认回对话。 */
  closeTo?: string;
  children: ReactNode;
}) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [titleOpen, setTitleOpen] = useState(false);
  const [titleClipped, setTitleClipped] = useState(false);
  const titleRef = useRef<HTMLElement | null>(null);

  const setTitleMeasureNode = useCallback((node: HTMLElement | null) => {
    titleRef.current = node;
  }, []);

  const measureTitle = useCallback(() => {
    const element = titleRef.current;
    const clipped = Boolean(element && element.scrollWidth > element.clientWidth + 1);
    setTitleClipped((current) => (current === clipped ? current : clipped));
  }, []);

  useLayoutEffect(() => {
    let active = true;
    const element = titleRef.current;
    measureTitle();

    const observer = element && typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(measureTitle)
      : null;
    if (element) observer?.observe(element);

    const handleResize = () => measureTitle();
    window.addEventListener("resize", handleResize);
    void document.fonts?.ready.then(() => {
      if (active) measureTitle();
    });

    return () => {
      active = false;
      observer?.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [measureTitle, title, titleClipped]);

  useEffect(() => {
    if (!titleClipped) setTitleOpen(false);
  }, [titleClipped]);

  useEffect(() => {
    setTitleOpen(false);
  }, [title]);

  const openFullTitle = () => {
    if (!titleClipped) return;
    setHelpOpen(false);
    setTitleOpen(true);
  };

  return (
    <section className="page rise-in">
      <header className="page__header">
        <div className="page__heading">
          {icon ? (
            <span className="page__icon" aria-hidden>
              {icon}
            </span>
          ) : null}
          <h1 className="page__title">
            {titleClipped ? (
              <button
                ref={setTitleMeasureNode}
                type="button"
                className="page__title-trigger"
                aria-label={`查看完整标题：${title}`}
                aria-haspopup="dialog"
                onClick={openFullTitle}
              >
                {title}
              </button>
            ) : (
              <span ref={setTitleMeasureNode} className="page__title-content">
                {title}
              </span>
            )}
          </h1>
          {help ? (
            <button
              type="button"
              className="page__help"
              onClick={() => {
                setTitleOpen(false);
                setHelpOpen(true);
              }}
              aria-label={`${title}是什么`}
            >
              ?
            </button>
          ) : null}
        </div>

        {actions ? <div className="page__actions">{actions}</div> : null}

        <Link
          to={closeTo}
          className="page__close"
          aria-label={closeTo === "/" ? "关闭" : "返回上一层"}
        >
          <IconClose size={20} />
        </Link>
      </header>

      <div className="page__body scroll-area">{children}</div>

      {help ? (
        <Sheet open={helpOpen} title={title} onClose={() => setHelpOpen(false)}>
          <div className="help-text">{help}</div>
        </Sheet>
      ) : null}
      <Sheet
        open={titleOpen}
        title={<span className="page__full-title">{title}</span>}
        label={title}
        headerDivider={false}
        onClose={() => setTitleOpen(false)}
      />
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* 弹层                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * 弹层。**一律居中**，不贴底。
 *
 * 这里曾经有 `variant: "sheet" | "dialog"` 两种：sheet 从底部升起、dialog 居中。
 * 已合并成一种。底部升起那种在这个产品里是错的——屏幕下半部本来就被对话框
 * 占着，弹层再从同一个方向长出来，两块东西叠在一起分不清谁是谁。
 *
 * ## `title` 可以带图标，但那样就必须传 `label`
 *
 * 旧版的心情日记与记忆碎片，标题两边各一颗实心心（`HeartTwoTone`），
 * 所以这里收 `ReactNode` 而不是 `string`。
 *
 * 代价是 `aria-label` 没法再从 title 里取——`<>♥心情日记♥</>` 拿不出文本。
 * 传了非字符串 title 就**必须**给 `label`，否则读屏软件念出来的是空的。
 */
export function Sheet({
  open,
  title,
  headerDivider = true,
  label,
  onClose,
  children,
}: {
  open: boolean;
  title: ReactNode;
  /** 标题栏下方是否显示分割线。 */
  headerDivider?: boolean;
  /** 无障碍名称。`title` 不是纯字符串时必传。 */
  label?: string;
  onClose: () => void;
  children?: ReactNode;
}) {
  if (!open) return null;

  const ariaLabel = label ?? (typeof title === "string" ? title : undefined);

  return (
    <div
      className="sheet-backdrop fade-in"
      role="presentation"
      onClick={onClose}
    >
      <div
        className={`sheet${headerDivider ? "" : " sheet--no-header-divider"}`}
        role="dialog"
        aria-modal="true"
        {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="sheet__header">
          <h2 className="sheet__title">{title}</h2>
          <button
            type="button"
            className="sheet__close"
            onClick={onClose}
            aria-label="关闭"
          >
            <IconClose size={18} />
          </button>
        </header>
        {children != null ? (
          <div className="sheet__body scroll-area">{children}</div>
        ) : null}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 分组                                                                        */
/* -------------------------------------------------------------------------- */

export function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">{title}</h2>
        {hint ? <p className="section__hint">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* 未知数值                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * 数值槽位。
 *
 * 拿不到就画一条占位横杠 + 一行原因，**绝不填 0，也不填缓存里的旧值**。
 * 灵力、羁绊、好感这些数字对用户是有意义的承诺：显示 0 会被读成「我被扣光了」，
 * 显示旧值会被读成「充值没到账」。空着反而是唯一诚实的呈现。
 *
 * 同理，前端也不本地计算权威关系值——所有数字都来自后端 State/Event。
 */
export function ValueSlot({
  label,
  value,
  unit,
  unavailableReason,
}: {
  label: string;
  value?: number | string | null;
  unit?: string;
  unavailableReason?: string;
}) {
  const known = value !== null && value !== undefined && value !== "";

  return (
    <div className={`value-slot${known ? "" : " value-slot--unknown"}`}>
      <p className="value-slot__label">{label}</p>
      {known ? (
        <p className="value-slot__value">
          {value}
          {unit ? <span className="value-slot__unit">{unit}</span> : null}
        </p>
      ) : (
        <p className="value-slot__value" aria-label="数值不可用">
          <span className="value-slot__dash" />
        </p>
      )}
      {!known && unavailableReason ? (
        <p className="value-slot__reason">{unavailableReason}</p>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 骨架                                                                        */
/* -------------------------------------------------------------------------- */

export function Skeleton({
  lines = 3,
  className = "",
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={`skeleton ${className}`.trim()} aria-hidden>
      {Array.from({ length: lines }, (_, index) => (
        <span
          key={index}
          className="skeleton__line"
          style={{ width: `${100 - index * 12}%` }}
        />
      ))}
    </div>
  );
}
